<script setup lang="ts">
import DivisionsWidget from '@/components/widgets/divisions-widget/DivisionsWidget.vue'
// import RadialBarChart from '@/components/widgets/common/RadialBarChart.vue'
import RadialBarChartTwo from '@/components/widgets/common/RadialBarChartTwo.vue'
import LineChart from '@/components/widgets/common/LineChart.vue'
import ArrotexLogo from '@/components/icons/ArrotexLogo.vue'
import VidacorpLogo from '@/components/icons/VidacorpLogo.vue'
import ApohealthLogo from '@/components/icons/ApohealthLogo.vue'
import ChemistsOwnLogo from '@/components/icons/ChemistsOwnLogo.vue'
import AxeLogo from '@/components/icons/AxeLogo.vue'
import { ref, watch, computed, onMounted } from 'vue'
import { storeToRefs } from 'pinia'
import { useThemeStore, useValueDataStore } from '@/stores'
import {
  transformDataToRadialChartFormat,
  transformDbgDataToRadialChartFormat
} from '@/components/common/common'
import PortfolioWidget from '../widgets/portfolio-widget/PortfolioWidget.vue'
import ProductsTable from '../widgets/portfolio-widget/ProductsTable.vue'
import SpecialtyBrandsTable from '../widgets/portfolio-widget/SpecialtyBrandsTable.vue'
import type { PharmaProgramsProduct } from '@/data'
import PharmaProgramsWidget from '../widgets/pharma-programs-widget/PharmaProgramsWidget.vue'
import AmgevitaLogo from '../icons/AmgevitaLogo.vue'
import HadlimaLogo from '../icons/HadlimaLogo.vue'
import ProliaLogo from '../icons/ProliaLogo.vue'
import SteqeymaLogo from '@/components/icons/SteqeymaLogo.vue'
import DbgHealthLogo from '../icons/DbgHealthLogo.vue'
import { HealthBoardIcon, HealthCareIcon, PeopleIcon, PillsIcon } from '@/components/icons'
import AboutWidget from '../widgets/about-widget/AboutWidget.vue'

defineProps<{
  dbgNetValue: boolean
  arrotexValue: boolean
  vidacorpValue: boolean
  axeValue: boolean
  dbgAbout: boolean
}>()

const themeStore = useThemeStore()
const { themeColors } = themeStore

const arrotexColorProps = computed(() => ({
  primary: themeColors.colorArrotex,
  secondary: themeColors.colorArrotexDark,
  tertiary: themeColors.colorText
}))

const vidacorpColorProps = computed(() => ({
  primary: themeColors.colorVidacorp,
  secondary: themeColors.colorVidacorpDark,
  tertiary: themeColors.colorText
}))

const aboutWidgets = [
  {
    isMain: true,
    number: '‎',
    title: 'DBG\nHEALTH',
    bullets: ['Pharmaceuticals', 'Consumer Health', 'Health Services'],
    captions: [
      { rank: '1', text: 'Pharmacy Pharma Company' },
      { rank: '1', text: 'Hospital Pharma Company' },
      { rank: '1', text: 'Private Label OTC Medicines Company' },
      { rank: '1', text: 'Cosmetics Company' }
    ],
    path: '/benefits',
    logoComponent: DbgHealthLogo,
    iconComponent: HealthCareIcon,
    logoProps: { height: '4.6875rem', width: '7.8125rem', fill: themeColors.colorBackgroundBright }
  },
  {
    number: '01',
    title: 'PHARMA-CEUTICAL',
    path: '/benefits/arrotex',
    bullets: ['Generic Pharmaceuticals', 'Branded Pharmaceuticals', 'Biologics and Biosimilars'],
    captions: [
      { rank: '1', text: 'Pharmacy Pharma Company' },
      { rank: '1', text: 'Hospital Pharma Company' },
      { rank: '1', text: 'Brand Pharma Company' }
    ],
    color: '#2AACE2',
    logoComponent: ArrotexLogo,
    iconComponent: PillsIcon,
    logoProps: { width: '7.8125rem', height: '3.125rem', fill: themeColors.colorText }
  },
  {
    number: '02',
    title: 'CONSUMER HEALTH',
    path: '/benefits/vidacorp',
    bullets: [
      'Private Label OTC Medicines',
      'Branded OTC Medicines',
      'Beauty Products',
      'Skincare'
    ],
    captions: [
      { rank: '1', text: 'Private Label OTC Medicines Company' },
      { rank: '1', text: 'Cosmetics Company' }
    ],
    color: '#2CCE86',
    logoComponent: VidacorpLogo,
    iconComponent: PeopleIcon,
    logoProps: { width: '7.8125rem', height: '3.125rem', fill: themeColors.colorText }
  },
  {
    number: '03',
    title: 'HEALTH SERVICES',
    path: '/benefits/axe',
    bullets: [
      'Pharma Consulting',
      'Health Programs',
      'Vaccines',
      'Nurse Services',
      'Business Data Services',
      'Medical Detailing'
    ],
    captions: [
      { text: 'Largest Medical Detailing Field Force' },
      { text: 'Leading Pharmacy Medicines Program' }
    ],
    color: '#5A5BC1',
    logoComponent: AxeLogo,
    iconComponent: HealthBoardIcon,
    logoProps: { width: '4.6875rem', height: '3.125rem', fill: themeColors.colorText }
  }
]

const { valueData } = storeToRefs(useValueDataStore())

const dbgTransformedData = ref(transformDbgDataToRadialChartFormat(valueData.value))

const arrotexTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.arrotex, 'arrotex')
)
const vidacorpTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.vidacorp, 'vidacorp')
)
const pharmaprogramsTransformedData = ref(
  transformDataToRadialChartFormat(valueData.value.pharmaprograms, 'axe')
)

function getArrotexOpportunityProducts(selectedPortfolio: string) {
  if (!selectedPortfolio) return []

  const opportunityProducts =
    valueData.value.arrotex.portfolioValues
      .find((portfolio: any) => portfolio.portfolioName === selectedPortfolio)
      ?.opportunityProducts?.map((p) => {
        if (['Generics', 'Biologics'].includes(selectedPortfolio)) {
          return {
            sku: p.sku,
            opportunityUnits: p.opportunityUnits,
            opportunityValue: p.opportunityValue,
            loyalty: p.loyalty,
            substitution: p.substitution
          }
        } else if (selectedPortfolio === 'Specialty Brands') {
          return {
            sku: p.sku,
            opportunityUnits: p.opportunityUnits,
            opportunityValue: p.opportunityValue,
            franchiseShare: p.franchiseShare,
            substitution: p.substitution
          }
        }
        return null // Explicitly return null for unmatched cases
      })
      ?.filter((p): p is NonNullable<typeof p> => p !== null) ?? []

  return opportunityProducts
}

function getVidacorpOpportunityProducts(selectedPortfolio: string) {
  if (!selectedPortfolio) return []

  const opportunityProducts =
    valueData.value.vidacorp.portfolioValues
      .find((portfolio: any) => portfolio.portfolioName === selectedPortfolio)
      ?.opportunityProducts?.map((p) => {
        if (['Apohealth', `Chemists' Own`, 'Health & Wellness'].includes(selectedPortfolio)) {
          return {
            sku: p.sku,
            opportunityValue: p.opportunityValue
          }
        }
        return null // Explicitly return null for unmatched cases
      })
      ?.filter((p): p is NonNullable<typeof p> => p !== null) ?? [] // Remove null values

  return opportunityProducts
}

const headerLogos = computed(() => [
  {
    portfolioName: 'Apohealth',
    logo: ApohealthLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorText }
  },
  {
    portfolioName: `Chemists' Own`,
    logo: ChemistsOwnLogo,
    logoProps: { height: '3.75rem', width: '9.375rem', fill: themeColors.colorText }
  }
])

const KNOWN_AXE_BRANDS = ['Amgevita', 'Hadlima', 'Prolia', 'SteQeyma']
const pharmaProgramsData = ref<PharmaProgramsProduct[]>([])
const chartMaxHeight = ref<number>(0)

const updateData = () => {
  if (valueData.value) {
    dbgTransformedData.value = transformDbgDataToRadialChartFormat(valueData.value)
    arrotexTransformedData.value = transformDataToRadialChartFormat(
      valueData.value.arrotex,
      'arrotex'
    )
    vidacorpTransformedData.value = transformDataToRadialChartFormat(
      valueData.value.vidacorp,
      'vidacorp'
    )
    pharmaprogramsTransformedData.value = transformDataToRadialChartFormat(
      valueData.value.pharmaprograms,
      'axe'
    )
    const programs = valueData.value.pharmaprograms || []
    const known = programs.filter((p) => KNOWN_AXE_BRANDS.includes(p.brandName))
    const others = programs.filter((p) => !KNOWN_AXE_BRANDS.includes(p.brandName))

    if (others.length) {
      const allSeries = others.flatMap((p) => p.scriptsAndClaimsSeries || [])
      const seriesByMonth: Record<string, { scripts: number; claims: number; month: string }> = {}

      allSeries.forEach((item) => {
        if (!seriesByMonth[item.month]) {
          seriesByMonth[item.month] = { scripts: 0, claims: 0, month: item.month }
        }
        seriesByMonth[item.month]!.scripts += item.scripts || 0
        seriesByMonth[item.month]!.claims += item.claims || 0
      })

      const aggregatedSeries = Object.values(seriesByMonth).sort((a, b) =>
        a.month.localeCompare(b.month)
      )

      const aggregatedOther: PharmaProgramsProduct = {
        brandName: 'Other',
        claimsEarnedValue: others.reduce((sum, p) => sum + (p.claimsEarnedValue || 0), 0),
        claimsEarnedValueCurrentQuarter: others.reduce(
          (sum, p) => sum + (p.claimsEarnedValueCurrentQuarter || 0),
          0
        ),
        claimsOpportunityValue: others.reduce((sum, p) => sum + (p.claimsOpportunityValue || 0), 0),
        scripts: others.reduce((sum, p) => sum + (p.scripts || 0), 0),
        claims: others.reduce((sum, p) => sum + (p.claims || 0), 0),
        scriptsVersusPreviousQuarter: others.reduce(
          (sum, p) => sum + (p.scriptsVersusPreviousQuarter || 0),
          0
        ),
        claimsVersusPreviousQuarter: others.reduce(
          (sum, p) => sum + (p.claimsVersusPreviousQuarter || 0),
          0
        ),
        averageFee:
          others.length > 0
            ? others.reduce((sum, p) => sum + ((p as any).averageFee || 0), 0) / others.length
            : 0,
        scriptsAndClaimsSeries: aggregatedSeries,
      }

      pharmaProgramsData.value = [...known, aggregatedOther]
    } else {
      pharmaProgramsData.value = programs
    }

    chartMaxHeight.value =
      pharmaProgramsData.value.reduce((max, item) => {
        const maxScriptsInSeries = Math.max(
          ...item.scriptsAndClaimsSeries.map((series: any) => series.scripts)
        )
        return Math.max(max, maxScriptsInSeries)
      }, 0) * 1.5
  }
}

watch(valueData, updateData)

onMounted(updateData)
</script>

<template>
  <div class="main-export-wrapper" id="export-views">
    <div v-if="dbgAbout" class="page-wrapper">
      <div class="customer-name">{{ valueData.customerName }} - About DBG</div>
      <div class="content-wrapper">
        <AboutWidget v-for="widget in aboutWidgets" :key="widget.path" v-bind="widget">
          <template #logo>
            <component :is="widget.logoComponent" v-bind="widget.logoProps" />
          </template>
          <template #icon>
            <component :is="widget.iconComponent" class="icon" />
          </template>
        </AboutWidget>
      </div>
    </div>
    <div v-if="dbgNetValue" class="page-wrapper">
      <div class="customer-name">{{ valueData.customerName }} - Total DBG Benefits</div>
      <div class="content-wrapper">
        <DivisionsWidget
          :isMain="true"
          :totalValue="valueData.portfolioNetValue.dbgValue"
          :currentQuarterValue="valueData.portfolioNetValue.dbgValueCurrentQuarter"
          :opportunityValue="valueData.portfolioNetValue.dbgNetOpportunity"
        >
          <template #value-title
            ><DbgHealthLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorBackgroundBright"
          /></template>
          <template #chart>
            <LineChart
              :data="valueData.portfolioNetValue.dbgTrend"
              :chartLegendValues="[
                { label: 'Arrotex', value: valueData.arrotex.value },
                { label: 'VidaCorp', value: valueData.vidacorp.value },
                {
                  label: 'PharmaPrograms',
                  value: valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsEarnedValue, 0)
                }
              ]"
            />
          </template>
          <template #opportunity-title><span class="title">Net Opportunity</span></template>
        </DivisionsWidget>

        <DivisionsWidget
          :isMain="false"
          :totalValue="valueData.arrotex.value"
          :currentQuarterValue="valueData.arrotex.valueCurrentQuarter"
          :opportunityValue="valueData.arrotex.opportunity"
        >
          <template #value-title>
            <ArrotexLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText" />
          </template>
          <template #chart>
            <RadialBarChartTwo
              :chartOptions="{ rotate: 'yes', backgroundCircleColor: '#fff' }"
              :data="arrotexTransformedData"
              :showLegend="true"
            />
          </template>
          <template #opportunity-title><span class="title">Arrotex Opportunity</span></template>
        </DivisionsWidget>

        <DivisionsWidget
          :isMain="false"
          :totalValue="valueData.vidacorp.value"
          :currentQuarterValue="valueData.vidacorp.valueCurrentQuarter"
          :opportunityValue="valueData.vidacorp.opportunity"
        >
          <template #value-title>
            <VidacorpLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText" />
          </template>
          <template #chart>
            <RadialBarChartTwo
              :chartOptions="{ rotate: 'yes', backgroundCircleColor: '#fff' }"
              :data="vidacorpTransformedData"
              :showLegend="true"
            />
          </template>
          <template #opportunity-title><span class="title">VidaCorp Opportunity</span></template>
        </DivisionsWidget>

        <DivisionsWidget
          :isMain="false"
          :totalValue="valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsEarnedValue, 0)"
          :currentQuarterValue="
            valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsEarnedValueCurrentQuarter, 0)
          "
          :opportunityValue="
            valueData.pharmaprograms.reduce((sum, i) => sum + i.claimsOpportunityValue, 0)
          "
        >
          <template #value-title>
            <AxeLogo width="4.6875rem" height="3.125rem" :fill="themeColors.colorText" />
          </template>
          <template #chart>
            <RadialBarChartTwo
              :chartOptions="{ rotate: 'yes', backgroundCircleColor: '#fff' }"
              :data="pharmaprogramsTransformedData"
              :showLegend="true"
            />
          </template>
          <template #opportunity-title><span class="title">Axe Opportunity</span></template>
        </DivisionsWidget>
      </div>
    </div>
    <div v-if="arrotexValue">
      <div
        v-for="(portfolio, index) in valueData.arrotex.portfolioValues"
        :key="index"
        class="page-wrapper"
      >
        <div class="customer-name">
          {{ valueData.customerName }} - Arrotex {{ portfolio.portfolioName }}
        </div>
        <div class="content-wrapper">
          <div class="page-left">
            <DivisionsWidget
              :isMain="false"
              :totalValue="valueData.arrotex.value"
              :currentQuarterValue="valueData.arrotex.valueCurrentQuarter"
              :opportunityValue="valueData.arrotex.opportunity"
            >
              <template #value-title
                ><ArrotexLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText"
              /></template>
              <template #chart>
                <RadialBarChartTwo
                  :chartOptions="{ rotate: 'yes', backgroundCircleColor: '#fff' }"
                  :data="arrotexTransformedData"
                  :showLegend="true"
                />
              </template>
              <template #opportunity-title><span class="title">Arrotex Opportunity</span></template>
            </DivisionsWidget>

            <PortfolioWidget
              :data="valueData.arrotex.portfolioValues"
              :colors="arrotexColorProps"
              :expandedIndex="index"
            >
              <template #logo>
                <ArrotexDots />
              </template>
            </PortfolioWidget>
          </div>
          <div class="page-right">
            <SpecialtyBrandsTable v-if="portfolio.portfolioName === 'Specialty Brands'" />
            <ProductsTable
              v-else
              :data="getArrotexOpportunityProducts(portfolio.portfolioName)"
              :portfolioName="portfolio.portfolioName"
              :color="themeColors.colorArrotex"
            >
            </ProductsTable>
          </div>
        </div>
      </div>
    </div>
    <div v-if="vidacorpValue">
      <div
        v-for="(portfolio, index) in valueData.vidacorp.portfolioValues"
        :key="index"
        class="page-wrapper"
      >
        <div class="customer-name">{{ valueData.customerName }} - VidaCorp {{ portfolio.portfolioName }}</div>
        <div class="content-wrapper">
          <div class="page-left">
            <DivisionsWidget
              :isMain="false"
              :totalValue="valueData.vidacorp.value"
              :currentQuarterValue="valueData.vidacorp.valueCurrentQuarter"
              :opportunityValue="valueData.vidacorp.opportunity"
            >
              <template #value-title
                ><VidacorpLogo width="7.8125rem" height="3.125rem" :fill="themeColors.colorText"
              /></template>
              <template #chart>
                <RadialBarChartTwo
                  :chartOptions="{ rotate: 'yes', backgroundCircleColor: '#fff' }"
                  :data="vidacorpTransformedData"
                  :showLegend="true"
                />
              </template>
              <template #opportunity-title
                ><span class="title">VidaCorp Opportunity</span></template
              >
            </DivisionsWidget>

            <PortfolioWidget
              :data="valueData.vidacorp.portfolioValues"
              :colors="vidacorpColorProps"
              :headerLogos="headerLogos"
              :expandedIndex="index"
            ></PortfolioWidget>
          </div>
          <div class="page-right">
            <ProductsTable
              :data="getVidacorpOpportunityProducts(portfolio.portfolioName)"
              :color="themeColors.colorVidacorp"
              :portfolioName="portfolio.portfolioName"
            >
            </ProductsTable>
          </div>
        </div>
      </div>
    </div>
    <div v-if="axeValue" class="page-wrapper">
      <div class="customer-name">{{ valueData.customerName }} - AXE PharmaPrograms</div>
      <div class="widgets-container">
        <PharmaProgramsWidget
          :isMain="false"
          v-if="pharmaProgramsData.find((i) => i.brandName === 'Amgevita')"
          :data="pharmaProgramsData.find((i) => i.brandName === 'Amgevita')!"
          color="#2569B1"
          :chartMaxHeight="chartMaxHeight"
        >
          <template #logo>
            <AmgevitaLogo width="10.9375rem" height="4.6875rem" :fill="themeColors.colorText" />
          </template>
        </PharmaProgramsWidget>
        <PharmaProgramsWidget
          :isMain="false"
          v-if="pharmaProgramsData.find((i) => i.brandName === 'Hadlima')"
          :data="pharmaProgramsData.find((i) => i.brandName === 'Hadlima')!"
          color="#B51E5E"
          :chartMaxHeight="chartMaxHeight"
        >
          <template #logo>
            <HadlimaLogo width="8.125rem" height="3.125rem" :fill="themeColors.colorText" />
          </template>
        </PharmaProgramsWidget>
        <PharmaProgramsWidget
          :isMain="false"
          v-if="pharmaProgramsData.find((i) => i.brandName === 'Prolia')"
          :data="pharmaProgramsData.find((i) => i.brandName === 'Prolia')!"
          color="#117026"
          :chartMaxHeight="chartMaxHeight"
        >
          <template #logo>
            <ProliaLogo width="9.0625rem" height="3.125rem" :fill="themeColors.colorText" />
          </template>
        </PharmaProgramsWidget>
        <PharmaProgramsWidget
          :isMain="false"
          v-if="pharmaProgramsData.find((i) => i.brandName === 'SteQeyma')"
          :data="pharmaProgramsData.find((i) => i.brandName === 'SteQeyma')!"
          color="#B0220E"
          :chartMaxHeight="chartMaxHeight"
        >
          <template #logo>
            <SteqeymaLogo width="8.75rem" height="3.75rem" :fill="themeColors.colorText" />
          </template>
        </PharmaProgramsWidget>
        <PharmaProgramsWidget
          :isMain="false"
          v-if="pharmaProgramsData.find((i) => i.brandName === 'Other')"
          :data="pharmaProgramsData.find((i) => i.brandName === 'Other')!"
          color="#252C78"
          :chartMaxHeight="chartMaxHeight"
        >
          <template #logo>
            <span class="title" :style="{ width: '10.3125rem', height: '3.125rem' }">
              All Others
            </span>
          </template>
        </PharmaProgramsWidget>
      </div>
    </div>
  </div>
</template>

<style scoped>
.page-wrapper {
  display: flex;
  flex-direction: column;
  page-break-after: always;
}
.customer-name {
  font-size: 1.75rem;
  font-weight: 700;
  align-self: center;
  color: var(--vt-c-indigo);
}
.content-wrapper {
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: space-around;
  gap: 2.5%;
}

.page-left,
.page-right {
  display: flex;
  flex: 0.5;
  flex-direction: row;
  justify-content: space-around;
  gap: 2.5%;
}

.widgets-container {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  width: 100%;
  gap: 2%;
  page-break-after: auto;
}

.title {
  font-size: 1.125rem;
  font-weight: 600;
}

.icon {
  font-size: 2.25rem;
  width: 30%;
}
</style>
